import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { 
  Search, 
  Filter, 
  PhoneCall, 
  PhoneIncoming, 
  PhoneOutgoing, 
  Calendar,
  Clock,
  RefreshCw
} from 'lucide-react'
import { toast } from 'react-toastify'
import { format } from 'date-fns'
import { callsAPI } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import LoadingSpinner from '../components/LoadingSpinner'
import CallDetailsModal from '../components/CallDetailsModal'
import clsx from 'clsx'

function Calls() {
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [selectedCall, setSelectedCall] = useState(null)
  const [page, setPage] = useState(1)
  const { user } = useAuth()
  const queryClient = useQueryClient()

  const queryParams = {
    page,
    limit: 20,
    ...(statusFilter && { status: statusFilter }),
    ...(searchTerm && { phoneNumber: searchTerm }),
    ...(dateFrom && { dateFrom }),
    ...(dateTo && { dateTo })
  }

  const { data: calls, isLoading } = useQuery(
    ['calls', queryParams],
    () => callsAPI.getCalls(queryParams),
    { keepPreviousData: true }
  )

  const { data: callStats } = useQuery(
    ['call-stats', { dateFrom, dateTo }],
    () => callsAPI.getCallStats({ dateFrom, dateTo })
  )

  const syncMutation = useMutation(callsAPI.syncCalls, {
    onSuccess: (data) => {
      toast.success(data.data.message)
      queryClient.invalidateQueries('calls')
      queryClient.invalidateQueries('call-stats')
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Sync failed')
    }
  })

  const handleSync = () => {
    const syncData = {}
    if (dateFrom) syncData.dateFrom = dateFrom
    if (dateTo) syncData.dateTo = dateTo
    syncMutation.mutate(syncData)
  }

  const getStatusBadge = (status) => {
    const baseClasses = 'status-badge'
    
    switch (status) {
      case 'completed':
        return `${baseClasses} status-active`
      case 'failed':
      case 'busy':
      case 'no-answer':
        return `${baseClasses} status-inactive`
      case 'in-progress':
      case 'ringing':
        return `${baseClasses} status-pending`
      default:
        return `${baseClasses} bg-primary-100 text-primary-800`
    }
  }

  const getDirectionIcon = (direction) => {
    return direction === 'inbound' ? PhoneIncoming : PhoneOutgoing
  }

  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A'
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const clearFilters = () => {
    setSearchTerm('')
    setStatusFilter('')
    setDateFrom('')
    setDateTo('')
    setPage(1)
  }

  const stats = callStats?.data?.summary || {}

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-primary-900">Call History</h1>
          <p className="mt-1 text-sm text-primary-600">
            View and analyze your call activity and performance
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
          {user?.role === 'admin' && (
            <button
              type="button"
              onClick={handleSync}
              disabled={syncMutation.isLoading}
              className="btn-secondary"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              {syncMutation.isLoading ? 'Syncing...' : 'Sync Calls'}
            </button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <PhoneCall className="h-8 w-8 text-blue-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-primary-500 truncate">
                    Total Calls
                  </dt>
                  <dd className="text-lg font-medium text-primary-900">
                    {stats.totalCalls || 0}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <PhoneIncoming className="h-8 w-8 text-green-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-primary-500 truncate">
                    Inbound
                  </dt>
                  <dd className="text-lg font-medium text-primary-900">
                    {stats.inboundCalls || 0}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <PhoneOutgoing className="h-8 w-8 text-purple-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-primary-500 truncate">
                    Outbound
                  </dt>
                  <dd className="text-lg font-medium text-primary-900">
                    {stats.outboundCalls || 0}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Clock className="h-8 w-8 text-orange-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-primary-500 truncate">
                    Avg Duration
                  </dt>
                  <dd className="text-lg font-medium text-primary-900">
                    {formatDuration(Math.round(stats.averageDuration || 0))}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-body">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
            <div>
              <label className="block text-sm font-medium text-primary-700 mb-1">
                Search Phone Number
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-primary-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="input-field pl-10"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-primary-700 mb-1">
                Status
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="input-field"
              >
                <option value="">All statuses</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
                <option value="busy">Busy</option>
                <option value="no-answer">No Answer</option>
                <option value="in-progress">In Progress</option>
                <option value="ringing">Ringing</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-primary-700 mb-1">
                From Date
              </label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="input-field"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-primary-700 mb-1">
                To Date
              </label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="input-field"
              />
            </div>

            <div className="flex items-end">
              <button
                type="button"
                onClick={clearFilters}
                className="btn-secondary w-full"
              >
                <Filter className="h-4 w-4 mr-2" />
                Clear Filters
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Calls Table */}
      {calls?.data?.calls?.length === 0 ? (
        <div className="text-center py-12">
          <PhoneCall className="mx-auto h-12 w-12 text-primary-400" />
          <h3 className="mt-2 text-sm font-medium text-primary-900">No calls found</h3>
          <p className="mt-1 text-sm text-primary-500">
            {searchTerm || statusFilter || dateFrom || dateTo
              ? 'No calls match your current filters.'
              : 'No call history available.'}
          </p>
        </div>
      ) : (
        <div className="card">
          <div className="overflow-hidden">
            <table className="min-w-full divide-y divide-primary-200">
              <thead className="bg-primary-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-primary-500 uppercase tracking-wider">
                    Call
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-primary-500 uppercase tracking-wider">
                    From/To
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-primary-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-primary-500 uppercase tracking-wider">
                    Duration
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-primary-500 uppercase tracking-wider">
                    Time
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-primary-200">
                {calls?.data?.calls?.map((call) => {
                  const DirectionIcon = getDirectionIcon(call.direction)
                  
                  return (
                    <tr 
                      key={call._id} 
                      className="hover:bg-primary-50 cursor-pointer"
                      onClick={() => setSelectedCall(call)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className={clsx(
                            'flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center',
                            call.direction === 'inbound' ? 'bg-green-100' : 'bg-blue-100'
                          )}>
                            <DirectionIcon className={clsx(
                              'h-4 w-4',
                              call.direction === 'inbound' ? 'text-green-600' : 'text-blue-600'
                            )} />
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-primary-900">
                              {call.phoneNumber}
                            </div>
                            <div className="text-sm text-primary-500 capitalize">
                              {call.direction}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-primary-900">
                          {call.direction === 'inbound' ? call.from : call.to}
                        </div>
                        <div className="text-sm text-primary-500">
                          {call.direction === 'inbound' ? 'from' : 'to'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={getStatusBadge(call.status)}>
                          {call.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-primary-900">
                        {formatDuration(call.duration)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-primary-500">
                        {format(new Date(call.startTime || call.createdAt), 'MMM d, yyyy HH:mm')}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {calls?.data?.pagination && calls.data.pagination.pages > 1 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-primary-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  className="btn-secondary"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page === calls.data.pagination.pages}
                  className="btn-secondary"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-primary-700">
                    Showing{' '}
                    <span className="font-medium">
                      {((page - 1) * calls.data.pagination.limit) + 1}
                    </span>{' '}
                    to{' '}
                    <span className="font-medium">
                      {Math.min(page * calls.data.pagination.limit, calls.data.pagination.total)}
                    </span>{' '}
                    of{' '}
                    <span className="font-medium">{calls.data.pagination.total}</span> results
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <button
                      onClick={() => setPage(page - 1)}
                      disabled={page === 1}
                      className="btn-secondary rounded-r-none"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setPage(page + 1)}
                      disabled={page === calls.data.pagination.pages}
                      className="btn-secondary rounded-l-none"
                    >
                      Next
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Call Details Modal */}
      <CallDetailsModal
        call={selectedCall}
        isOpen={!!selectedCall}
        onClose={() => setSelectedCall(null)}
      />
    </div>
  )
}

export default Calls